import { useGtmDataLayer } from '~/composables/gtm/useGtmDataLayer'

function getCategoryFromLine(line) {
  if (line?.productData?.item_category)
    return line.productData.item_category
  if (line?.productData?.categories?.length > 0)
    return line.productData.categories[0]
  return null
}

function buildTrackItem(
  product,
  price,
  quantity,
  page,
  event,
  item_list_id,
  category,
  product_id,
) {
  if (category == null && page?.value?.breadcrumbs?.length) {
    category = page.value.breadcrumbs[page.value.breadcrumbs.length - 2].text
  }

  const totalPrice = Number.parseFloat((price.incl_tax * quantity).toFixed(2))
  const item_id = (product.upc?.length
    ? product.upc
    : product?.children
      ? (product_id === null ? product.children[0]?.upc : product.children.find(p => p.id === product_id)?.upc)
      : product.id) ?? product.id

  const data = {
    event,
    ecommerce: {
      currency: 'CHF',
      value: totalPrice,
      items: [{
        item_id,
        item_name: product.title,
        currency: 'CHF',
        item_brand: 'LUMA Delikatessen',
        item_category: category,
        quantity,
        price: Number.parseFloat(Number.parseFloat(price.incl_tax).toFixed(2)),
      }],
    },
  }

  if (price && price.action) {
    data.ecommerce.items[0].discount = Number.parseFloat((Number.parseFloat(price?.original_incl_tax) - Number.parseFloat(price?.incl_tax)).toFixed(2))
  }

  if (item_list_id) {
    data.ecommerce.items[0].item_list_id = item_list_id
  }

  return data
}

/**
 * GTM ecommerce tracking. Single composable returning all ecom tracking methods.
 */
export function useEcomTracking() {
  const { dataLayer } = useGtmDataLayer()

  function trackItemView(product, price, quantity, page) {
    if (import.meta.server) {
      return
    }

    dataLayer?.push({ ecommerce: null })
    dataLayer?.push(buildTrackItem(product, price, quantity, page, 'view_item', null, product.item_category, null))
  }

  function trackAddToCart(product, price, itemListId, quantity, category, product_id = null) {
    if (import.meta.server) {
      return
    }

    dataLayer?.push({ ecommerce: null })
    dataLayer?.push(buildTrackItem(product, price, quantity, null, 'add_to_cart', itemListId, category, product_id))
  }

  function trackRemoveFromCart(product, price, itemListId, quantity, category = null) {
    if (import.meta.server) {
      return
    }

    dataLayer?.push({ ecommerce: null })
    dataLayer?.push(buildTrackItem(product, price, quantity, null, 'remove_from_cart', itemListId, category, null))
  }

  function trackViewCart(items, value) {
    if (import.meta.server) {
      return
    }

    const productList = items.map((item) => {
      const price = item.productData?.price
      const category = getCategoryFromLine(item)
      const entry = {
        item_id: item.productData.upc,
        item_name: item.productData.title,
        currency: 'CHF',
        item_brand: 'LUMA Delikatessen',
        item_category: category,
        quantity: item.quantity,
        price: Number.parseFloat(price?.incl_tax),
      }

      if (price?.action) {
        entry.discount = Number.parseFloat((Number.parseFloat(price?.original_incl_tax) - Number.parseFloat(price.incl_tax)).toFixed(2))
      }

      return entry
    })

    dataLayer?.push({ ecommerce: null })
    dataLayer?.push({ event: 'view_cart', ecommerce: { currency: 'CHF', value: Number.parseFloat(value), items: productList } })
  }

  function trackBeginCheckout(items, value) {
    if (import.meta.server) {
      return
    }

    const productList = items.map((item) => {
      const category = getCategoryFromLine(item)
      const price = item.productData?.price
      const entry = {
        item_id: item.productData.upc,
        item_name: item.productData.title,
        currency: 'CHF',
        item_brand: 'LUMA Delikatessen',
        item_category: category,
        quantity: item.quantity,
        price: Number.parseFloat(Number.parseFloat(price?.incl_tax).toFixed(2)),
      }

      if (price?.action) {
        entry.discount = Number.parseFloat((Number.parseFloat(price?.original_incl_tax) - Number.parseFloat(price.incl_tax)).toFixed(2))
      }

      return entry
    })

    dataLayer?.push({ ecommerce: null })
    dataLayer?.push({ event: 'begin_checkout', ecommerce: { currency: 'CHF', value: Number.parseFloat(value), items: productList } })
  }

  function trackPurchase(items, basket, customerAddress, customer, shippingCost, paymentType, customerIsNew, voucherCode, transactionId, _optionalSummary = null) {
    if (import.meta.server) {
      return
    }

    if (typeof window !== 'undefined' && basket.id === window.basket_id) {
      return
    }

    if (typeof window !== 'undefined') {
      window.basket_id = basket.id
    }

    const productList = items.map((item) => {
      const category = getCategoryFromLine(item)
      const price = item.productData.price
      const entry = {
        item_id: item.productData.upc,
        item_name: item.productData.title,
        currency: 'CHF',
        item_brand: 'LUMA Delikatessen',
        item_category: category,
        quantity: item.quantity,
        price: Number.parseFloat(Number.parseFloat(price?.incl_tax).toFixed(2)),
      }

      if (price && price.action) {
        entry.discount = Number.parseFloat((Number.parseFloat(price?.original_incl_tax) - Number.parseFloat(price.incl_tax)).toFixed(2))
      }

      return entry
    })

    dataLayer?.push({ ecommerce: null })
    dataLayer?.push({
      event: 'purchase',
      ecommerce: {
        currency: 'CHF',
        value: Number.parseFloat((Number.parseFloat(basket.total_incl_tax) + Number.parseFloat(shippingCost)).toFixed(2)),
        items: productList,
        transaction_id: transactionId,
        tax: Number.parseFloat(basket.total_tax),
        shipping: Number.parseFloat(shippingCost),
        coupon: voucherCode,
        payment_type: paymentType,
      },
      customer: {
        new: customerIsNew,
        email: customer.email,
        phone_number: customer.phone,
        address: {
          first_name: customerAddress.first_name,
          last_name: customerAddress.last_name,
          street: `${customerAddress.street} ${customerAddress.house_number}`,
          city: customerAddress.city,
          postal_code: customerAddress.postcode,
        },
      },
    })
  }

  function trackCustomerInfo(items, value, checkoutType) {
    if (import.meta.server || !items) {
      return
    }

    const productList = items.map((item) => {
      const price = item.productData.price
      const category = getCategoryFromLine(item)
      const entry = {
        item_id: item.productData.upc,
        item_name: item.productData.title,
        currency: 'CHF',
        item_brand: 'LUMA Delikatessen',
        item_category: category,
        quantity: item.quantity,
        price: Number.parseFloat(Number.parseFloat(price?.incl_tax).toFixed(2)),
      }

      if (price && price.action) {
        entry.discount = Number.parseFloat((Number.parseFloat(price?.original_incl_tax) - Number.parseFloat(price.incl_tax)).toFixed(2))
      }

      return entry
    })

    dataLayer?.push({ ecommerce: null })
    dataLayer?.push({ event: 'add_customer_information', ecommerce: { currency: 'CHF', value: Number.parseFloat(value), items: productList, checkout_type: checkoutType } })
  }

  function trackCustomerAddress(items, value, shippingTier) {
    if (import.meta.server) {
      return
    }

    const productList = items.map((item) => {
      const category = getCategoryFromLine(item)
      const price = item.productData.price
      const entry = {
        item_id: item.productData.upc,
        item_name: item.productData.title,
        currency: 'CHF',
        item_brand: 'LUMA Delikatessen',
        item_category: category,
        quantity: item.quantity,
        price: Number.parseFloat(Number.parseFloat(price?.incl_tax).toFixed(2)),
      }

      if (price && price.action) {
        entry.discount = Number.parseFloat((Number.parseFloat(price?.original_incl_tax) - Number.parseFloat(price.incl_tax)).toFixed(2))
      }

      return entry
    })

    dataLayer?.push({ ecommerce: null })
    dataLayer?.push({ event: 'add_shipping_info', ecommerce: { currency: 'CHF', value: Number.parseFloat(value), items: productList, shipping_tier: shippingTier } })
  }

  function trackSelectItem(item, itemListId, itemListName, index, price) {
    if (import.meta.server) {
      return
    }

    const data = buildTrackItem(item, price, 1, null, 'select_item', itemListId, item.type, null)
    data.ecommerce.item_list_name = itemListName
    data.ecommerce.items[0].index = index

    dataLayer?.push({ ecommerce: null })
    dataLayer?.push(data)
  }

  return {
    trackItemView,
    trackAddToCart,
    trackRemoveFromCart,
    trackViewCart,
    trackBeginCheckout,
    trackPurchase,
    trackCustomerInfo,
    trackCustomerAddress,
    trackSelectItem,
  }
}
